import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Flex from '../Flex';
import PillTag from '../Pilltag';
import Icon from '../Icon';
import theme from '../theme';
import { CarouselNavButtons } from '../Carousel/useCarouselNav';

const SCROLL_AMOUNT = 200;

const Wrapper = styled(Flex)`
  position: relative;
`;

const ScrollContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  overflow-x: auto;
  flex-wrap: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-behavior: smooth;
  &::-webkit-scrollbar {
    display: none;
  }

  mask-image: ${({ $canScrollLeft, $canScrollRight }) => {
    if ($canScrollLeft && $canScrollRight) return 'linear-gradient(to right, transparent, black 40px, black calc(100% - 40px), transparent)';
    if ($canScrollLeft) return 'linear-gradient(to right, transparent, black 40px)';
    if ($canScrollRight) return 'linear-gradient(to left, transparent, black 40px)';
    return 'none';
  }};
`;

const CategoryFilter = ({ categories, selectedCategorySlug, onSelect }) => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState, categories]);

  const scroll = (direction) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: direction * SCROLL_AMOUNT });
  };

  const showArrows = canScrollLeft || canScrollRight;

  return (
    <Wrapper gap='10' align='center'>
      <Icon.Filter size='small' color={theme.colors.NEU_600} style={{ flexShrink: 0 }} />
      <ScrollContainer ref={scrollRef} $canScrollLeft={canScrollLeft} $canScrollRight={canScrollRight}>
        <PillTag label={t('coursioNetwork.allCategories')} selected={selectedCategorySlug === null} onClick={() => onSelect(null)} />
        {categories.map((category) => (
          <PillTag
            key={category.slug}
            label={t(`categories.labels.${category.slug}`, category.name)}
            selected={selectedCategorySlug === category.slug}
            onClick={() => onSelect(category.slug)}
          />
        ))}
      </ScrollContainer>
      {showArrows && <CarouselNavButtons onPrev={() => scroll(-1)} onNext={() => scroll(1)} disablePrev={!canScrollLeft} disableNext={!canScrollRight} />}
    </Wrapper>
  );
};

CategoryFilter.displayName = 'CategoryFilter';

export default CategoryFilter;
